import { Fragment } from 'react'

import { FormSection } from '../common'
import composerStyles from './PostComposer.module.css'
import { validatePostComposerStep } from './postComposerModel'

// Display labels differ per consumer (Individual calls the bet step "Bet",
// Command Center calls it "Market") while the registry remains the only source
// for section identity, ordering, validation, and rendering metadata.
const COMMAND_CENTER_LABELS = {
  content: 'Content',
  market: 'Market',
  distribution: 'Distribution',
}

function onePageWrapper(id, content) {
  return (
    <section id={`post-composer-section-${id}`} className={composerStyles.onePageSection}>
      {content}
    </section>
  )
}

function isSectionComplete(stepIndex, currentStepIndex) {
  return stepIndex < currentStepIndex
}

// The one section registry Guided and One page both filter/map instead of
// owning parallel conditional trees. Each entry names its id, how to render
// it in a given layout mode, how to validate it, when it counts complete, and
// where to focus/scroll on error -- Command Center extends this array with
// its own review-only entry rather than duplicating the shared three.
export const POST_COMPOSER_SECTION_REGISTRY = [
  {
    id: 'content',
    labels: { individual: 'Content', commandCenter: COMMAND_CENTER_LABELS.content },
    focusId: 'post-composer-section-content',
    validate: validatePostComposerStep,
    complete: isSectionComplete,
    render: ({ mode, content }) => (mode === 'one-page' ? onePageWrapper('content', content) : content),
  },
  {
    id: 'market',
    labels: { individual: 'Bet', commandCenter: COMMAND_CENTER_LABELS.market },
    focusId: 'post-composer-section-market',
    validate: validatePostComposerStep,
    complete: isSectionComplete,
    render: ({ mode, content, expanded, onToggleExpanded }) => (
      mode === 'one-page'
        ? onePageWrapper('market', (
          <FormSection
            title="Attach a bet (optional)"
            headingSize="subsection"
            collapsible
            expanded={expanded}
            onToggle={onToggleExpanded}
            className={composerStyles.onePageDisclosure}
          >
            {content}
          </FormSection>
        ))
        : content
    ),
  },
  {
    id: 'distribution',
    labels: { individual: 'Publish', commandCenter: COMMAND_CENTER_LABELS.distribution },
    focusId: 'post-composer-section-distribution',
    validate: validatePostComposerStep,
    complete: isSectionComplete,
    render: ({ mode, content }) => (mode === 'one-page' ? onePageWrapper('distribution', content) : content),
  },
]


export function sectionLabel(entry, consumer) {
  return entry.labels[consumer] || entry.id
}

// Guided and One page both call this to turn the registry into the
// {value, label, complete} shape the shared Stepper and jump-nav expect.
export function buildWorkflowSteps(registry, currentStepIndex, consumer) {
  return registry.map((entry, index) => ({
    value: entry.id,
    label: sectionLabel(entry, consumer),
    complete: entry.complete(index, currentStepIndex),
  }))
}

// One rendering entry point for both layout modes: One page renders every
// registry entry (each deciding its own wrapper), Guided renders only the
// active entry. This replaces hand-rolled parallel guided/one-page trees.
export function renderComposerSections({
  registry,
  mode,
  activeStep,
  sections,
  marketExpanded,
  onToggleMarketExpanded,
  afterSections,
}) {
  const renderProps = (entry) => ({
    mode,
    content: sections[entry.id],
    expanded: entry.id === 'market' ? marketExpanded : undefined,
    onToggleExpanded: entry.id === 'market' ? onToggleMarketExpanded : undefined,
  })

  if (mode === 'one-page') {
    return registry.map((entry) => (
      <Fragment key={entry.id}>
        {entry.render(renderProps(entry))}
        {afterSections?.[entry.id]}
      </Fragment>
    ))
  }

  const active = registry.find((entry) => entry.id === activeStep)
  return active ? active.render(renderProps(active)) : null
}
